// Which data sources are off, and which are blocked, for the Overview
// (2026-09-30). A source is "unavailable" when the tenant blocks it, not
// when the customer turned it off:
//   - license: the portal's license state (config.licenseDisabled, written by
//     backend/lib/license-state.mjs), with the reason it stored;
//   - permission: the last run reported the source as failed with a
//     permission error (Graph 403 and similar).
// Everything else that is not enabled is "turned off" by the customer (or by
// the tier defaults they can change in Sources).

import { COLLECTOR_CATEGORIES, licenseRequirement } from '../../../shared/collectors.mjs';

const LABELS = Object.fromEntries(COLLECTOR_CATEGORIES.flatMap((c) => c.collectors).map((c) => [c.id, c.label]));
const ORDER = COLLECTOR_CATEGORIES.flatMap((c) => c.collectors).map((c) => c.id);

const PERMISSION_ERROR = /\b(403|forbidden|unauthori[sz]ed|permission|privileges|access denied|authorization_requestdenied)\b/i;

/**
 * @param {{ enabledCollectors?: string[], licenseDisabled?: Record<string, { reason?: string }> }|null} config
 * @param {{ failed?: Array<{ name: string, error?: string, license?: boolean }> }|null} lastRun
 * @returns {{ turnedOff: Array<{ id: string, label: string }>, unavailable: Array<{ id: string, label: string, reason: string }> }}
 */
export function dataSourceStates(config, lastRun) {
  const enabled = new Set(config?.enabledCollectors ?? []);
  const unavailable = new Map();

  for (const [id, entry] of Object.entries(config?.licenseDisabled ?? {})) {
    if (!(id in LABELS)) continue;
    unavailable.set(id, entry?.reason || licenseRequirement(id) || 'A license is required');
  }
  for (const f of lastRun?.failed ?? []) {
    const id = typeof f === 'string' ? f : f?.name;
    if (!(id in LABELS) || unavailable.has(id)) continue;
    if (f?.license) {
      unavailable.set(id, licenseRequirement(id) || 'A license is required');
    } else if (PERMISSION_ERROR.test(String(f?.error ?? ''))) {
      unavailable.set(id, `Missing permission: ${String(f.error).slice(0, 160)}`);
    }
  }

  const rank = (id) => ORDER.indexOf(id);
  return {
    turnedOff: ORDER.filter((id) => !enabled.has(id) && !unavailable.has(id)).map((id) => ({ id, label: LABELS[id] })),
    unavailable: [...unavailable]
      .map(([id, reason]) => ({ id, label: LABELS[id], reason }))
      .sort((a, b) => rank(a.id) - rank(b.id)),
  };
}

const runId = (f) => (typeof f === 'string' ? f : f?.name);

/**
 * Per-source status of one collection (ENTRAID-347 "Collections today" tiles), in catalog order. The sources in scope
 * are the ones the customer turned on, the ones the run reported, and the ones the tenant blocks; a source the
 * customer turned off is not pending and is left out.
 *   collected  the run collected it;
 *   failed     the run reported it failed, for a reason other than a license or a permission;
 *   pending    it needs a license or a permission (dataSourceStates `unavailable`), or it has not run yet.
 * A source id the catalog does not know (an older or newer collector) keeps its id as the label, after the catalog.
 *
 * @returns {Array<{ id: string, label: string, status: 'collected'|'pending'|'failed' }>}
 */
export function sourceStatuses(config, lastRun) {
  const blocked = new Set(dataSourceStates(config, lastRun).unavailable.map((u) => u.id));
  const succeeded = new Set((lastRun?.succeeded ?? []).map(runId).filter((id) => typeof id === 'string' && id));
  const failed = new Set((lastRun?.failed ?? []).map(runId).filter((id) => typeof id === 'string' && id));
  const scope = new Set([...(config?.enabledCollectors ?? []), ...succeeded, ...failed, ...blocked]);
  const statusOf = (id) => {
    if (blocked.has(id) && !succeeded.has(id)) return 'pending';
    if (failed.has(id)) return 'failed';
    if (succeeded.has(id)) return 'collected';
    return 'pending';
  };
  const known = ORDER.filter((id) => scope.has(id));
  const unknown = [...scope].filter((id) => typeof id === 'string' && id && !(id in LABELS)).sort();
  return [...known, ...unknown].map((id) => ({ id, label: LABELS[id] ?? id, status: statusOf(id) }));
}

/** `{ collected, pending, failed }` counts of sourceStatuses(). */
export function countStatuses(statuses) {
  const out = { collected: 0, pending: 0, failed: 0 };
  for (const s of statuses ?? []) if (s?.status in out) out[s.status] += 1;
  return out;
}
